import { PersistedActionCodec, IPersistedAction, makeStreamPosition } from '@engoo/realtime-protocol/persisted';
import {
  PresenceActionCodec,
  PresenceActionType,
  IPresenceJoinAction,
  IPresenceUnjoinAction,
  IPresenceNameChangeAction,
} from '@engoo/realtime-protocol/presence';
import { fold } from 'fp-ts/lib/Either';
import { pipe } from 'fp-ts/lib/pipeable';
import { Map, OrderedMap, Record } from 'immutable';
import * as t from 'io-ts';
import { makeTimestamp } from '@engoo/realtime-protocol/util/timeSchemas';
import { IAnonymousPeerName, IProvidedPeerName } from '@engoo/realtime-protocol/peerName';

type PersistedPresenceJoinAction = IPresenceJoinAction & IPersistedAction;
type PersistedPresenceUnjoinAction = IPresenceUnjoinAction & IPersistedAction;
type PersistedPresenceNameChangeAction = IPresenceNameChangeAction & IPersistedAction;

export type PersistedPresenceAction = PersistedPresenceJoinAction |
  PersistedPresenceUnjoinAction |
  PersistedPresenceNameChangeAction;

export class PresenceJoin extends Record<PersistedPresenceJoinAction>({
  peerId: '',
  position: makeStreamPosition(0),
  timestamp: makeTimestamp(0),
  type: PresenceActionType.Join,
  name: { type: 'unresolved' },
}) {
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  public constructor(props: PersistedPresenceJoinAction) {
    super(props);
  }
}

export class PresenceUnjoin extends Record<PersistedPresenceUnjoinAction>({
  peerId: '',
  position: makeStreamPosition(0),
  timestamp: makeTimestamp(0),
  type: PresenceActionType.Unjoin,
}) {
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  public constructor(props: PersistedPresenceUnjoinAction) {
    super(props);
  }
}

export class PresenceNameChange extends Record<PersistedPresenceNameChangeAction>({
  peerId: '',
  position: makeStreamPosition(0),
  timestamp: makeTimestamp(0),
  type: PresenceActionType.NameChange,
  name: { type: 'unresolved' },
}) {
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  public constructor(props: PersistedPresenceNameChangeAction) {
    super(props);
  }
}

export type PresenceChange = PresenceJoin | PresenceUnjoin | PresenceNameChange;
type ResolvedPeerName = IProvidedPeerName | IAnonymousPeerName;

export class PresenceState extends Record<{
  events: OrderedMap<number, PresenceChange>,
  /**
   * Record in what order peers joined the first time, use this to
   * determine selection color
   */
  joinOrder: Map<string, number>,
  participantNames: Map<string, ResolvedPeerName>,
}>({
  events: OrderedMap(),
  joinOrder: Map(),
  participantNames: Map(),
}) {}

const updatePresenceState = (state: PresenceState, action: PresenceChange) => {
  const mutable = state.asMutable();
  const { position, peerId } = action;

  mutable.update<'events'>('events', (events) => events.set(position, action));

  if (!mutable.joinOrder.has(peerId)) {
    mutable.update<'joinOrder'>('joinOrder', (joinOrder) => {
      return joinOrder.set(peerId, joinOrder.size);
    });
  }

  return mutable.asImmutable();
};

const updateParticipantName = (state: PresenceState, action: PresenceJoin | PresenceNameChange) => {
  const nameData = action.name;

  if (nameData.type === 'unresolved') {
    return state;
  }

  return state.update('participantNames', (nameMap) => nameMap.set(action.peerId, nameData));
};

export const presenceReducer = (state = new PresenceState(), action?: unknown): PresenceState => {
  return pipe(
    t.intersection([PresenceActionCodec, PersistedActionCodec]).decode(action),
    fold(
      () => {
        return state;
      },
      (persistedPresenceAction) => {
        switch (persistedPresenceAction.type) {
          case PresenceActionType.Join: {
            const joinAction = new PresenceJoin(persistedPresenceAction);
            return updatePresenceState(updateParticipantName(state, joinAction), joinAction);
          }
          case PresenceActionType.Unjoin:
            return updatePresenceState(state, new PresenceUnjoin(persistedPresenceAction));
          case PresenceActionType.NameChange: {
            const nameChangeAction = new PresenceNameChange(persistedPresenceAction);
            return updatePresenceState(updateParticipantName(state, nameChangeAction), nameChangeAction);
          }
        }
      },
    ),
  );
};
